import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Ship, Loader2, AlertCircle } from "lucide-react";

export function LoginPage() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const res = await login(username, password);
      if (!res.ok) {
        setError(res.error || "Login failed");
      }
    } catch (err) {
      setError("An unexpected error occurred");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4 relative overflow-hidden">
      {/* Background decorations for a premium look */}
      <div className="absolute top-0 right-0 -mr-20 -mt-20 h-[40rem] w-[40rem] rounded-full bg-primary/5 blur-3xl" />
      <div className="absolute bottom-0 left-0 -ml-20 -mb-20 h-[40rem] w-[40rem] rounded-full bg-blue-500/5 blur-3xl" />

      <div className="relative z-10 w-full max-w-md space-y-8 rounded-2xl border border-white/5 dark:border-white/10 bg-background/60 p-8 shadow-2xl backdrop-blur-xl ring-1 ring-black/5 dark:ring-white/10">
        <div className="flex flex-col items-center space-y-3 text-center">
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-primary to-primary/80 shadow-lg shadow-primary/25 ring-1 ring-primary/20 transition-transform hover:scale-105">
            <Ship className="h-7 w-7 text-primary-foreground" strokeWidth={2.5} />
          </div>
          <div className="space-y-1">
            <h1 className="text-2xl font-bold tracking-tight text-foreground sm:text-3xl">
              Welcome Back
            </h1>
            <p className="text-sm text-muted-foreground">
              Sign in to access the Simulation UI workspace
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6 mt-8">
          {error && (
            <div className="flex items-center gap-3 rounded-xl bg-destructive/10 p-3.5 text-sm text-destructive border border-destructive/20 animate-in fade-in slide-in-from-top-2">
              <AlertCircle className="h-4 w-4 shrink-0" />
              <p className="font-medium">{error}</p>
            </div>
          )}

          <div className="space-y-4">
            <div className="space-y-2">
              <label
                htmlFor="username"
                className="text-sm font-semibold leading-none text-foreground/90"
              >
                Username
              </label>
              <input
                id="username"
                type="text"
                required
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="flex h-11 w-full rounded-xl border border-input/50 bg-background/50 px-4 py-2 text-sm shadow-sm transition-colors placeholder:text-muted-foreground/60 focus-visible:border-primary focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary disabled:cursor-not-allowed disabled:opacity-50"
                placeholder="Enter your username"
              />
            </div>

            <div className="space-y-2">
              <label
                htmlFor="password"
                className="text-sm font-semibold leading-none text-foreground/90"
              >
                Password
              </label>
              <input
                id="password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="flex h-11 w-full rounded-xl border border-input/50 bg-background/50 px-4 py-2 text-sm shadow-sm transition-colors placeholder:text-muted-foreground/60 focus-visible:border-primary focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary disabled:cursor-not-allowed disabled:opacity-50"
                placeholder="••••••••"
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="group relative inline-flex w-full items-center justify-center overflow-hidden rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground shadow-sm transition-all hover:bg-primary/90 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50"
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Signing in...
              </>
            ) : (
              "Sign in"
            )}
            {!loading && (
              <div className="absolute inset-0 -mx-4 flex items-center justify-center opacity-0 transition-opacity group-hover:opacity-20">
                <div className="h-full w-12 bg-white/40 blur-xl skew-x-12 translate-x-[-150%] group-hover:translate-x-[150%] transition-transform duration-1000" />
              </div>
            )}
          </button>
        </form>
      </div>
    </div>
  );
}
